import React, { useState, useEffect } from "react";
import ImageUploader from "./ImageUpload";
import MapDetailsForm from "./MapDetailsForm";
import InteractiveTables from "./InteractiveTables";
import axios from "axios";
import Compressor from "compressorjs";

const MapsManager = () => {
  const [maps, setMaps] = useState([]);
  const [mapNames, setMapNames] = useState({});
  const [submittedNames, setSubmittedNames] = useState({});
  const [tableData, setTableData] = useState({});
  const [imageDimensions, setImageDimensions] = useState({});
  const [isLoaded, setIsLoaded] = useState(false); // To track if architecture is loaded
  const [isNewArchitecture, setIsNewArchitecture] = useState(true); // To track if we are creating a new architecture
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const adminId = localStorage.getItem("adminId");
  const clubId = JSON.parse(localStorage.getItem("clubIds"))[0];

  // Fetch the saved architecture on component mount
  useEffect(() => {
    const loadSavedArchitecture = async () => {
      try {
        let token = localStorage.getItem("token");
        const response = await axios.get(
          `${BACKEND_URL}/tables/table-architecture/${clubId}`,
          {
            headers: {
              authorization: `Bearer ${token}`
            }
          }
        );

        const { maps } = response.data;

        if (maps && maps.length > 0) {
          const newMaps = maps.map((map) => ({
            mapName: map.mapName,
            dataUrl: map.imageUrl, // Assuming imageUrl has been saved to Azure
            tables: map.tables
          }));
          setMaps(newMaps);

          const newMapNames = {};
          const newSubmittedNames = {};
          const newImageDimensions = {};
          const newTableData = {};

          // Load each image independently and set its corresponding table data once loaded
          newMaps.forEach((map, index) => {
            const img = new Image();
            img.src = map.dataUrl;

            img.onload = () => {
              newImageDimensions[index] = {
                width: img.width,
                height: img.height
              };

              // Set table data based on percentage values after image dimensions are known
              newTableData[index] = map.tables.map((table) => ({
                id: table.tableNumber,
                name: table.tableName,
                people: table.seats,
                price: table.price,
                pricesPerDay: table.pricesPerDay,
                daysOfWeek: table.daysOfWeek,
                color: table.color,
                xPercentage: (table.x / img.width) * 100,
                yPercentage: (table.y / img.height) * 100,
                widthPercentage: (table.width / img.width) * 100,
                heightPercentage: (table.height / img.height) * 100
              }));

              // Update image dimensions, table data, and map names as they are ready
              setImageDimensions((prevDimensions) => ({
                ...prevDimensions,
                [index]: newImageDimensions[index]
              }));

              setTableData((prevTableData) => ({
                ...prevTableData,
                [index]: newTableData[index]
              }));

              newMapNames[index] = map.mapName;
              newSubmittedNames[index] = map.mapName;
              setMapNames((prevNames) => ({
                ...prevNames,
                ...newMapNames
              }));

              setSubmittedNames((prevSubmittedNames) => ({
                ...prevSubmittedNames,
                ...newSubmittedNames
              }));
            };
          });

          setIsNewArchitecture(false);
        } else {
          console.log("NO MASP FOUND ");
        }
      } catch (error) {
        console.log(
          "No existing architecture found, allowing creation of new one."
        );
      } finally {
        setIsLoaded(true);
      }
    };

    loadSavedArchitecture();
  }, [clubId, BACKEND_URL]);

  const base64ToBlob = (base64, mime) => {
    const byteString = atob(base64.split(",")[1]);
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const uint8Array = new Uint8Array(arrayBuffer);

    for (let i = 0; i < byteString.length; i++) {
      uint8Array[i] = byteString.charCodeAt(i);
    }

    return new Blob([uint8Array], { type: mime });
  };

  // Handles image upload and sets the maps state
  const handleImagesLoaded = (loadedImages) => {
    const compressedImages = [];

    loadedImages.forEach((map) => {
      const blob = base64ToBlob(map.dataUrl, "image/jpeg"); // Assuming it's a JPEG image
      new Compressor(blob, {
        quality: 0.1,
        success(result) {
          const reader = new FileReader();
          reader.readAsDataURL(result);
          reader.onloadend = () => {
            compressedImages.push({
              name: map.name,
              dataUrl: reader.result
            });
            if (compressedImages.length === loadedImages.length) {
              setMaps(compressedImages);
            }
          };
        },
        error(err) {
          console.error(err.message);
        }
      });
    });

    setMaps(loadedImages);

    // Set the dimensions for each image
    const dimensions = loadedImages.reduce((acc, map, index) => {
      const img = new Image();
      img.src = map.dataUrl;
      img.onload = () => {
        acc[index] = { width: img.width, height: img.height };
      };
      return acc;
    }, {});
    setImageDimensions(dimensions);
  };

  // Handle map name changes
  const handleMapNameChange = (index, name) => {
    setMapNames((prevNames) => ({
      ...prevNames,
      [index]: name
    }));
  };

  const handleNameSubmit = (index) => {
    setSubmittedNames((prevSubmitted) => ({
      ...prevSubmitted,
      [index]: mapNames[index]
    }));
  };

  const handleRemoveMap = (index) => {
    setMaps((prevMaps) => prevMaps.filter((_, i) => i !== index));

    setMapNames((prevNames) => {
      const newNames = { ...prevNames };
      delete newNames[index];
      return newNames;
    });

    setSubmittedNames((prevSubmitted) => {
      const newSubmitted = { ...prevSubmitted };
      delete newSubmitted[index];
      return newSubmitted;
    });
  };

  const handleTableDataChange = (index, tables) => {
    setTableData((prevData) => ({
      ...prevData,
      [index]: tables
    }));
  };

  const convertToAbsoluteValues = (tables, imageWidth, imageHeight) => {
    return tables.map((table) => ({
      tableName: table.name,
      tableNumber: table.id,
      seats: parseInt(table.people),
      pricesPerDay:
        table.pricingType === "universal"
          ? Array(7).fill(parseFloat(table.price))
          : table.pricesPerDay.map((price) => parseFloat(price) || 0), // Handle empty strings or invalid inputs
      daysOfWeek:
        table.pricingType === "universal"
          ? [
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
            "Sunday"
          ]
          : [
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
            "Sunday"
          ],
      x: (table.xPercentage / 100) * imageWidth,
      y: (table.yPercentage / 100) * imageHeight,
      width: (table.widthPercentage / 100) * imageWidth,
      height: (table.heightPercentage / 100) * imageHeight,
      color: table.color
    }));
  };

  // Submit the architecture data to the backend
  const handleSubmit = async () => {
    const formattedMaps = maps.map((map, index) => {
      const imageSize = imageDimensions[index];

      const tables = tableData[index]
        ? convertToAbsoluteValues(
          tableData[index],
          imageSize.width,
          imageSize.height
        )
        : [];

      return {
        mapName: submittedNames[index] || `Map ${index + 1}`,
        mapNumber: index + 1,
        imageUrl: map.dataUrl,
        tables
      };
    });

    const tableArchitectureData = {
      clubId,
      maps: formattedMaps
    };

    // Send the data to the backend
    try {
      let token = localStorage.getItem("token");
      const response = await axios.post(
        `${BACKEND_URL}/tables/table-architecture`,
        tableArchitectureData,
        {
          headers: {
            authorization: `Bearer ${token}`
          }
        }
      );
      console.log("Table architecture saved successfully:", response.data);
      alert("Table architecture saved successfully!");
    } catch (error) {
      console.error("Error saving table architecture:", error);
      alert("Error saving table architecture.");
    }
  };
  useEffect(() => {
    if (submittedNames && tableData && imageDimensions) {
    }
    console.log("maps", submittedNames, tableData, imageDimensions);
  }, [maps, submittedNames, tableData, imageDimensions]);

  return (
    <div>
      <h3>
        {isNewArchitecture
          ? "Upload your table maps:"
          : "View your table maps:"}
      </h3>

      {isLoaded && (
        <>
          {isNewArchitecture && (
            <ImageUploader onImagesLoaded={handleImagesLoaded} />
          )}
          <MapDetailsForm
            maps={maps}
            mapNames={mapNames}
            handleMapNameChange={handleMapNameChange}
            handleNameSubmit={handleNameSubmit}
          />

          {maps.map((map, index) => (
            <div key={index} style={{ marginTop: "20px" }}>
              {submittedNames[index] ? (
                <h3>{submittedNames[index]}</h3>
              ) : (
                <h3>Map {index + 1}</h3>
              )}

              <button
                style={{
                  backgroundColor: "red",
                  color: "white",
                  borderRadius: "5px",
                  padding: "5px",
                  marginBottom: "10px",
                  fontSize: "12px"
                }}
                onClick={() => handleRemoveMap(index)}
              >
                Remove Map {index + 1}
              </button>

              {/* Ensure tableData and imageDimensions are available before rendering DraggableTables */}
              {submittedNames[index] && imageDimensions[index] && (
                <InteractiveTables
                  imageUrl={map.dataUrl}
                  mapName={submittedNames[index]}
                  initialTables={tableData[index]} // Ensure tableData[index] exists
                  onTableDataChange={(tables) =>
                    handleTableDataChange(index, tables)
                  }
                />
              )}
            </div>
          ))}

          {isNewArchitecture && (
            <button onClick={handleSubmit}>Submit Architecture</button>
          )}
        </>
      )}
    </div>
  );
};

export default MapsManager;
